import { useMutation } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import type { components } from '@/api/generated/schema';

type Schemas = components['schemas'];

export type CreateEstablishmentInput = Schemas['PixlideApplicationFeaturesEstablishmentsCreateEstablishmentCommand'];
export type CreateScreenInput = Schemas['PixlideApplicationFeaturesScreensCreateScreenCommand'];
export type PairingCode = Schemas['PixlideApplicationFeaturesScreensPairingCodeResponse'];

/**
 * Le parc d'écrans, le quota consommé et le décompte par établissement changent ensemble :
 * une seule fonction d'invalidation, pour qu'aucun des trois ne reste périmé.
 */
function invalidateFleet(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.screens() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.establishments() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.subscription });
}

/** Déclare un établissement ; renvoie son identifiant. */
export function useCreateEstablishment() {
  return useMutation({
    mutationFn: async (input: CreateEstablishmentInput) => {
      const { data } = await api.POST('/api/v1/establishments', { body: input });

      return data!;
    },

    onSuccess: invalidateFleet,
  });
}

/** Déclare un écran ; refusé en 409 si le quota de la formule est atteint. */
export function useCreateScreen() {
  return useMutation({
    mutationFn: async (input: CreateScreenInput) => {
      const { data } = await api.POST('/api/v1/screens', { body: input });

      return data!;
    },

    onSuccess: invalidateFleet,
  });
}

/**
 * Génère un code d'appairage (15 minutes). Un nouvel appel REMPLACE le code précédent :
 * c'est ce qui permet de régénérer un code expiré sans autre manipulation.
 */
export function useStartScreenPairing() {
  return useMutation({
    mutationFn: async (screenId: string) => {
      const { data } = await api.POST('/api/v1/screens/{screenId}/pairing', {
        params: { path: { screenId } },
        // Même contournement que `useMarkNotificationRead` : FastEndpoints répond 415 sans
        // corps JSON, alors que le contrat OpenAPI n'en déclare aucun pour cette route.
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });

      return data!;
    },
  });
}

/**
 * Détache l'appareil d'un écran. Côté serveur, le secret de l'appareil est effacé et la
 * passerelle de diffusion lui demande aussitôt de revenir à l'écran d'appairage.
 */
export function useUnpairScreen() {
  return useMutation({
    mutationFn: async (screenId: string) => {
      await api.DELETE('/api/v1/screens/{screenId}/pairing', {
        params: { path: { screenId } },
      });
    },

    onSuccess: invalidateFleet,
  });
}
